import { LoadingButton } from "@mui/lab";
import {
  Typography,
  IconButton,
  AccordionDetails,
  PopoverOrigin,
  Popover,
  Accordion,
  AccordionSummary,
  Dialog,
  DialogTitle,
  Button,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  DialogContent,
  DialogContentText,
  useTheme,
  styled,
} from "@mui/material";
import Spacer from "../../Spacer";
import IOSSwitch from "../../IOSSwitch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { config } from "../../../config/config";
import {
  EFileType,
  ProjectDetailContext,
  UseSnackbarContext,
} from "../../../pages/ProjectDetail";
import { ProjectDetailUtil } from "../../Utility/ProjectDetailUtil";
import { makeStyles } from "@material-ui/core";
import { Common } from "../../../../common/src";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";

type RegDashboardProps = {
  open: boolean;
  close?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
};

const RegisterWithDashboard = (regDashboardProps: RegDashboardProps) => {
  const { projectDetail, isJapanese, getProjectDetail } =
    useContext(ProjectDetailContext);
  const { openSuccess, openError, openWarning } =
    useContext(UseSnackbarContext);

  /*****************************************
      state
    ******************************************/
  const [regFileType, setRegFileType] = useState<EFileType>(undefined); // Dashboard登録用ファイルタイプ
  const [whileUpload, setWhilUpload] = useState<boolean>(false);
  const [radioCoordinates, setRadioCoordinates] =
    useState<Common.POINT_CLOUD_TYPE>(Common.POINT_CLOUD_TYPE.SFM); //「点群を選択」
  const [shouldInterpolate, setShouldInterpolate] = useState<boolean>(true);

  /*****************************************
      state
    ******************************************/
  type CustomPopoverProps = {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
    content: string;
  };
  const CustomPopover = ({
    anchorOrigin,
    transformOrigin,
    content,
  }: CustomPopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const replaceNewLine = (s: String) => {
      return s.split("\n").map((line) => (
        <span>
          {line}
          <br />
        </span>
      ));
    };

    const open = Boolean(anchorEl);

    return (
      <div>
        <IconButton onClick={handleClick} style={{ padding: 0 }}>
          <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <Typography sx={{ p: 2 }} style={{ width: "600px" }}>
            {content ? replaceNewLine(content) : ""}
          </Typography>
        </Popover>
      </div>
    );
  };

  /*****************************************
      Effect
    ******************************************/
  useEffect(() => {
    setRegFileType(undefined);
    setShouldInterpolate(true);
  }, [regDashboardProps.open]);

  /*****************************************
      function
    ******************************************/
  const handleRegDashBoardCancel = () => {
    regDashboardProps.close();
  };

  const handleRegDashBoardOK = async () => {
    setWhilUpload(true);
    await regDashboard(regFileType)
      .then(() => {
        openSuccess(t("UPLOAD_DASHBOARD_SUCCESS"));
      })
      .catch((errMsg) => {
        switch (errMsg) {
          case "401":
            openError(t("NO_AUTHENTIFICATION"));
            break;
          case "404":
            openError(t("NO_AUTHENTIFICATION"));
            break;
          case "no_result":
            openError(t("FAIL_JOBSITE_CREATED"));
            break;
          case "no_authority":
            openError(t("NO_AUTHENTIFICATION"));
            break;
          case "504":
            openWarning(t("PLS_CONFIRM"));
            break;
          default:
            openError(t("ERROR"));
            break;
        }
      })
      .finally(() => {
        setWhilUpload(false);
        regDashboardProps.close();
        getProjectDetail();
      });
  };

  const regDashboard = (fileType: string) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await axios
          .post(
            `${config.serverHost}/api/v1/project/${projectDetail?.id}/upload/dashboard`,
            {
              fileType: fileType,
              pointType: radioCoordinates,
              shouldInterpolate: shouldInterpolate,
            },
            {
              withCredentials: true,
              onUploadProgress: (progressEvent) => {
                Math.round((progressEvent.loaded * 100) / progressEvent.total);
              },
            }
          )
          .catch((err) => {
            switch (err?.response?.status) {
              case 401:
                throw new Error("401");
              case 404:
                if (err.response.data === "no_result") {
                  throw new Error("no_result");
                } else if (err.response.data === "no_authority") {
                  throw new Error("no_authority");
                } else {
                  throw new Error("404");
                }
              case 504:
                throw new Error("504");
              default:
                throw new Error();
            }
          });
        return resolve();
      } catch (e: any) {
        reject(e?.message);
      }
    });
  };

  const disabledSave = () => {
    const status = projectDetail.status;
    const PROCESSING = Common.PROJECT_STATUS_ENUM.PROCESSING;
    return (
      !regFileType ||
      (status &&
        ((radioCoordinates === Common.POINT_CLOUD_TYPE.SFM &&
          (status.coordinate_transformation === PROCESSING ||
            status.point_splitting_sfm === PROCESSING ||
            status.remove_sfm === PROCESSING ||
            status.point_txt_sfm === PROCESSING)) ||
          (radioCoordinates === Common.POINT_CLOUD_TYPE.HELMERT &&
            (status.helmert_transformation === PROCESSING ||
              status.point_splitting_helmert === PROCESSING ||
              status.remove_helmert === PROCESSING ||
              status.point_txt_helmert === PROCESSING))))
    );
  };

  /*****************************************
      style
    ******************************************/
  const theme = useTheme();

  const customAccordionBackground = {
    background: theme.palette.base.modal,
  };

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    boxShadow: "none",
    color: theme.palette.base.contrast,
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const customLoadingButton = {
    background: theme.palette.primary.main,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.primary.main,
      boxShadow: "none",
    },
  };

  const cssStyles = makeStyles({
    dialogRoot: {
      "&.MuiTypography-root": { textAlign: "left", fontSize: "18px" },
    },
    selectStyle: {
      width: "538px",
      height: "35px",
      marginTop: "28px",
      right: "15px",
      textAlign: "left",
    },
    typographyStyle: { fontSize: "15px", textTransform: "none" },
    radioStyleComm: { display: "flex", justifyContent: "flex-start" },
    radioStyle1: { left: "20px" },
    radioStyle2: { left: "200px" },
    isLanguageRadioStyle2: { left: "20px", width: "450px", top: "110px" },
    btn1: {
      "&.MuiButtonBase-root": {
        height: "24px",
        width: "24px",
        color: customColor.color,
        float: "right",
        fontSize: "18px",
        margin: "0px",
        padding: "0px",
        position: "absolute",
        right: "0px",
        outline: "none",
      },
    },
    btn2: {
      "&.MuiButtonBase-root": {
        height: "42px",
        width: "532px",
        top: "69px",
        left: "24px",
        background: "#0064BA",
        color: customColor.color,
        position: "absolute",
        outline: "none",
      },
    },
    btn3: {
      "&.MuiButtonBase-root": {
        height: "42px",
        width: "532px",
        top: "69px",
        left: "24px",
        background: "#0064BA",
        marginTop: "60px",
        color: customColor.color,
        position: "absolute",
        outline: "none",
      },
    },
    btn4: {
      "&.MuiButtonBase-root": {
        height: "42px",
        width: "250px",
        color: customColor.color,
        position: "absolute",
        border: "1px solid #565664",
        outline: "none",
        marginRight: "30px",
        bottom: "15px",
        right: "280px",
      },
    },
    btn5: {
      "&.MuiButtonBase-root": {
        height: "42px",
        width: "250px",
        color: customColor.color,
        position: "absolute",
        outline: "none",
        marginRight: "14px",
        bottom: "15px",
        textTransform: "none",
      },
    },
  });
  const userStyle = cssStyles();

  return (
    <>
      <Dialog
        open={regDashboardProps.open}
        onClose={regDashboardProps.onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          className: "dashboard01",
          style: {
            border: "1px solid #44444F",
            ...customBackgroundStyles,
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: customDialogBackgroundColor.color,
            },
          },
        }}
      >
        <DialogTitle className="title-name" style={customBackgroundStyles}>
          {t("UPLOAD_SMART_DASHBOARD")}
          <Button
            style={customColor}
            className="btn-x"
            onClick={regDashboardProps.close}
          >
            ×
          </Button>
          <div className="border-comm" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={customColor}>
            {t("SELECT_POINT_CLOUD")}
          </DialogContentText>
          <DialogActions sx={{ display: "flex", justifyContent: "flex-start" }}>
            <RadioGroup sx={{ flexDirection: "inherit" }}>
              <FormControlLabel
                label={t("ORIGINAL_SFM_POINT")}
                disabled={
                  !projectDetail.useViDoc ||
                  !ProjectDetailUtil.isSfMComplete(projectDetail.status)
                }
                className={`${userStyle.radioStyleComm} ${userStyle.radioStyle1}`}
                control={
                  <Radio
                    checked={radioCoordinates === Common.POINT_CLOUD_TYPE.SFM}
                    onChange={() =>
                      setRadioCoordinates(Common.POINT_CLOUD_TYPE.SFM)
                    }
                  />
                }
              />
              <FormControlLabel
                label={t("TRANSFOR_SFM_POINT")}
                disabled={
                  !ProjectDetailUtil.isHelmartComplete(projectDetail.status)
                }
                className={
                  isJapanese
                    ? `${userStyle.radioStyleComm} ${userStyle.radioStyle2}`
                    : `${userStyle.radioStyleComm} ${userStyle.isLanguageRadioStyle2}`
                }
                control={
                  <Radio
                    checked={
                      radioCoordinates === Common.POINT_CLOUD_TYPE.HELMERT
                    }
                    onChange={() =>
                      setRadioCoordinates(Common.POINT_CLOUD_TYPE.HELMERT)
                    }
                  />
                }
              />
            </RadioGroup>
          </DialogActions>
          <DialogContentText style={customColor}>
            {t("SELECT_POINT_FORMAT")}
          </DialogContentText>
          <DialogActions sx={{ display: "flex", justifyContent: "flex-start" }}>
            <FormControlLabel
              label={".las"}
              className={`${userStyle.radioStyleComm} ${userStyle.radioStyle1}`}
              control={
                <Radio
                  checked={regFileType === EFileType.LAS}
                  onChange={() => setRegFileType(EFileType.LAS)}
                />
              }
            />
            <FormControlLabel
              label={".txt"}
              className={
                isJapanese
                  ? `${userStyle.radioStyleComm} ${userStyle.radioStyle2}`
                  : `${userStyle.radioStyleComm} ${userStyle.isLanguageRadioStyle2}`
              }
              control={
                <Radio
                  checked={regFileType === EFileType.TXT}
                  onChange={() => setRegFileType(EFileType.TXT)}
                />
              }
            />
          </DialogActions>

          <Accordion
            disableGutters={true}
            sx={{
              boxShadow: "none",
              marginBottom: "30px",
              "&:before": {
                display: "none",
              },
              ".MuiAccordionSummary-root": { justifyContent: "start" },
              ...customAccordionBackground,
            }}
          >
            <AccordionSummary
              sx={{
                minHeight: 30,
                height: 30,
                padding: 0,
                ".MuiAccordionSummary-content": { flexGrow: 0 },
              }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              {"【" + t("ADVANCED_SETTINGS") + "】"}
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div
                className="setting-select1"
                style={{ marginTop: 0, marginLeft: 10 }}
              >
                <div className="setting-text-left">
                  {t("INTERPOLATE_SETTING_C1")}
                </div>
                <div className="setting-text-right">
                  <CustomPopover
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    content={t("INTERPOLATE_SETTING_D2")}
                  />
                  <Spacer height={0} width={10} />
                  <IOSSwitch
                    onChange={(e) => {
                      setShouldInterpolate(e.target.checked);
                    }}
                    checked={shouldInterpolate}
                  />
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          <DialogActions
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0px",
            }}
          >
            <CustomCanselButton
              onClick={handleRegDashBoardCancel}
              className="dashboard04"
              variant="contained"
              size="small"
              sx={{ borderRadius: "0px" }}
            >
              {t("CANCEL")}
            </CustomCanselButton>
            <LoadingButton
              className="loading-white dashboard05"
              loading={whileUpload}
              disabled={disabledSave()}
              variant="contained"
              size="small"
              onClick={handleRegDashBoardOK}
              sx={{
                borderRadius: "0px",
                ...(disabledSave() ? null : customLoadingButton),
              }}
            >
              {t("UPLOAD")}
            </LoadingButton>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RegisterWithDashboard;
